import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyPlans } from "@/hooks/use-plans";
import { useCurrentUsage } from "@/hooks/use-current-usage";
import { Radio } from "lucide-react";
import { WidgetProps } from "@/lib/widgets/types";

export function TrackingUsageWidget({ config }: WidgetProps) {
  const { activeCompany } = useAuth();
  const { data: plans } = useCompanyPlans(activeCompany?.id);
  const { data: usage, isLoading } = useCurrentUsage(activeCompany?.id || '', 'gps_tracking');

  const currentPlan = plans?.find(p => p.system?.code === 'gps_tracking');
  const limits = currentPlan?.plan?.features?.limits || {};
  
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-sm text-muted-foreground">Carregando uso...</div>
        </CardContent>
      </Card>
    );
  }

  const getUsagePercentage = (current: number, limit: number) => {
    if (limit === 999999) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getVariant = (percentage: number): "default" | "destructive" | "secondary" => {
    if (percentage > 90) return "destructive";
    if (percentage > 70) return "secondary";
    return "default";
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radio className="h-5 w-5" />
          Uso TrackingONE
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(limits).length === 0 ? (
          <div className="text-sm text-muted-foreground">Nenhum plano ativo</div>
        ) : (
          Object.entries(limits).map(([key, limit]) => {
            const current = usage?.[key] || 0;
            const percentage = getUsagePercentage(current, limit);
            
            // Use labels genéricos para tracking
            const labelMap: Record<string, string> = {
              tracking_vehicles_max: 'Veículos Rastreados',
              tracking_devices_max: 'Dispositivos GPS',
              tracking_users_max: 'Usuários',
            };
            
            return (
              <div key={key} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {labelMap[key] || key}
                  </span>
                  <Badge variant={getVariant(percentage)}>
                    {current} / {limit === 999999 ? '∞' : limit}
                  </Badge>
                </div>
                <Progress value={percentage} />
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
